/**
 * paiementLauncher — point d'entrée UNIQUE pour ouvrir le tunnel de paiement.
 *
 * Historiquement, ~12 call-sites (CardDon / modales campagne / LinkMetaRender /
 * Services / Abonnements, sur 5 thèmes) faisaient à la main :
 *     sessionStorage.setItem("paiement_config", JSON.stringify(objet))
 *     router.push(org.BASE_WEB_URL + "/paiement")
 * avec chacun son propre shape et son propre `returnUrl` (parfois oublié). On
 * centralise ici pour poser de façon UNIFORME :
 *
 *   1. `source` : "APP" quand on tourne dans l'app native (window.ISAPP), sinon
 *      "WEB". Le backend (services/paiementLinks.js) lit ce champ via une
 *      whitelist stricte → segmentation compta don app vs web.
 *
 *   2. `returnUrl` : l'URL WEB où le donateur revient après le paiement (page
 *      courante par défaut). IDENTIQUE app et web. Dans l'app, le retour se fait
 *      par Universal Link : la page de succès (hébergée sur un AUTRE domaine que
 *      le site de l'org) redirige vers ce returnUrl → iOS/Android, voyant un lien
 *      du domaine de l'org, rouvrent l'app dessus (cf. unisoft_mobile : Associated
 *      Domains / intent-filter autoVerify + deepLink.js). Aucune logique deep link
 *      ici : on envoie toujours l'URL web pure.
 *
 * SSR-safe : tout accès à window est gardé (le helper peut être importé partout).
 */

import NativeBridge, { isApp } from "./NativeBridge"
import { isEmbedded, getHostUrl, navigateOut } from "./embedHost"
import { getPurOrg } from "../utils"
import logger from "./logger"

// Clé sessionStorage lue par la page /paiement (cf. themes/*/Paiement/index.js).
// Unique et partagée → constante ici pour éviter les fautes de frappe entre
// call-sites.
export const PAIEMENT_STORAGE_KEY = "paiement_config"

// URL web absolue de la page courante (comportement historique du returnUrl).
function currentHref() {
    if (typeof window === "undefined" || !window.location) return ""
    return window.location.href
}

/**
 * Domaine du site qui intègre l'org, tel que déclaré en configuration
 * (admin → Configuration du site → Intégration). Filet quand la page hôte ne
 * s'annonce pas : il donne le bon site, à défaut de la bonne page.
 */
function configuredParentUrl(org = {}) {
    const raw = org?.web?.["embed.parentDomain"]
    if (typeof raw !== "string" || !raw.trim()) return null
    const trimmed = raw.trim()
    try {
        const url = new URL(/^[a-z]+:\/\//i.test(trimmed) ? trimmed : "https://" + trimmed)
        if (url.protocol !== "http:" && url.protocol !== "https:") return null
        return url.href
    } catch (e) {
        return null
    }
}

/**
 * Retire l'ancre (#...) d'une adresse de retour.
 *
 * POURQUOI : le returnUrl repart en PARAMÈTRE d'une autre URL — celle de notre
 * page de succès, construite par concaténation côté API
 * (`.../success/?source=WEB&returnUrl=<ici>&id=...`). Une ancre y termine
 * l'adresse : tout ce qui suit devient du fragment, et la page de succès ne
 * reçoit plus ni `id` ni `cerfa`. Le cas est banal en intégration — le code
 * d'intégration se colle dans une section atteinte par `#don`, et c'est cette
 * adresse-là que la page hôte nous annonce.
 *
 * L'ancre n'a par ailleurs aucune valeur pour une destination de retour : elle
 * n'est jamais transmise au serveur. On la laisse tomber, on garde le reste.
 */
function sansFragment(url) {
    if (typeof url !== "string") return url
    const i = url.indexOf("#")
    return i === -1 ? url : url.slice(0, i)
}

/**
 * Construit le returnUrl à mettre dans le payload paiement = l'URL web où le
 * donateur revient après le paiement. TOUJOURS une URL WEB (page courante), aussi
 * bien sur navigateur que dans l'app : en app, le retour se fait par Universal
 * Link (la page de succès, sur un AUTRE domaine, redirige vers cette URL du domaine
 * de l'org → iOS/Android rouvrent l'app dessus). Aucune exception app ici.
 *
 * @param {object} org           org du context (porte BASE_WEB_URL en dernier recours)
 * @param {string} [fallbackUrl] URL web à utiliser (défaut = page courante). Sert aux
 *                               liens externes (LinkMetaRender) qui ont déjà un
 *                               returnUrl fourni par le backend.
 */
export function buildReturnUrl(org = {}, { forced, fromCallSite } = {}) {
    const base = (org && org.BASE_WEB_URL) || ""

    // Page du site hôte : la SEULE adresse qui ramène le donateur là où il
    // donnait. `window.location.href` ne vaut ici que l'URL de notre iframe —
    // le renvoyer dessus le sortirait du site de son organisation.
    const hostUrl = isEmbedded() ? (getHostUrl() || configuredParentUrl(org)) : null

    // Un returnUrl explicite reste prioritaire (lien créé côté backend avec sa
    // propre destination), SAUF quand il ne fait que répéter notre domaine —
    // c'est alors une valeur de repli, et la page hôte vaut mieux.
    if (forced && !(hostUrl && forced === base)) return sansFragment(forced)
    if (hostUrl) return sansFragment(hostUrl)

    return sansFragment(fromCallSite || currentHref() || base || "https://kehila.io")
}

// "WEB" | "APP" selon le contexte d'exécution.
export function resolveSource() {
    return isApp() ? "APP" : "WEB"
}

/**
 * Prépare le payload final à stocker : injecte organisation (pur), source et
 * returnUrl de façon uniforme, sans écraser ce que le call-site a déjà calculé
 * de spécifique (montant, type, campagne…). Renvoie l'objet enrichi.
 *
 * @param {object} config payload partiel construit par le call-site
 * @param {object} org    org du context (porte BASE_WEB_URL, devise, mobileConfig)
 * @param {object} [opts]
 *   - opts.forceReturnUrl : si fourni, sert de fallback web (liens externes).
 */
export function buildPaiementPayload(config = {}, org = {}, opts = {}) {
    return {
        ...config,
        // organisation : si le call-site l'a déjà mis (getPurOrg), on le garde ;
        // sinon on le dérive. getPurOrg ne retire PAS mobileConfig (utile au retour).
        organisation: config.organisation || getPurOrg(org),
        // source posée systématiquement (le backend whiteliste APP/WEB).
        source: resolveSource(),
        // returnUrl = URL web (page courante par défaut), identique app et web. On
        // part du returnUrl déjà présent dans config (souvent window.location.href
        // posé par le call-site) comme valeur, ou de opts.forceReturnUrl.
        returnUrl: buildReturnUrl(org, {
            forced: opts.forceReturnUrl,
            fromCallSite: config.returnUrl,
        }),
    }
}

// Paramètre d'URL qui transporte la configuration du don quand le stockage de
// session ne peut pas la porter (cf. encodePaiementConfig).
const PAIEMENT_CONFIG_PARAM = "pc"

// Au-delà, on renonce à passer par l'URL : un panier hors norme ne doit pas
// produire une adresse que le navigateur ou un proxy tronquerait en silence.
const MAX_CONFIG_URL_LENGTH = 6000

function toBase64Url(text) {
    const bytes = new TextEncoder().encode(text)
    let binary = ""
    for (const byte of bytes) binary += String.fromCharCode(byte)
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

function fromBase64Url(value) {
    const base64 = value.replace(/-/g, "+").replace(/_/g, "/")
    const binary = atob(base64)
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
    return new TextDecoder().decode(bytes)
}

/**
 * Encode la configuration du don pour la faire voyager DANS L'URL.
 *
 * POURQUOI : le chemin normal passe par le stockage de session, mais celui-ci
 * est cloisonné par site hôte. Quand le paiement quitte l'iframe pour s'ouvrir
 * en pleine fenêtre, la page d'arrivée est dans une autre partition et ne voit
 * rien de ce qu'on y a écrit. L'URL est le seul canal qui franchisse la sortie.
 *
 * L'organisation est retirée du paquet : c'est de loin le plus gros morceau, et
 * la page de paiement la reçoit déjà par son propre rendu serveur.
 */
function encodePaiementConfig(payload = {}) {
    try {
        const { organisation, ...rest } = payload
        const encoded = toBase64Url(JSON.stringify(rest))
        if (encoded.length > MAX_CONFIG_URL_LENGTH) {
            // Muet, ce renoncement produit une page de paiement vide : la sortie
            // de l'iframe a lieu quand même, et le stockage de session ne franchit
            // pas la frontière. On le journalise, et l'appelant renonce à sortir.
            logger.error(
                { length: encoded.length, max: MAX_CONFIG_URL_LENGTH },
                "[paiementLauncher] config trop volumineuse pour l'URL"
            )
            return null
        }
        return encoded
    } catch (e) {
        logger.error({ err: e }, "[paiementLauncher] encodage de la config impossible")
        return null
    }
}

/**
 * Hôtes vers lesquels cette org accepte de renvoyer un donateur.
 *
 * Miroir client de `api/services/return_url.js` : mêmes sources, même régime.
 * Tant que l'org n'a déclaré aucun domaine d'intégration, on n'impose rien
 * (c'est ce que promet l'admin, et la page hôte est alors la seule à savoir où
 * renvoyer le donateur) ; dès qu'elle en déclare, on s'y tient.
 */
function orgReturnHosts(org = {}) {
    const hosts = new Set()
    const add = (value) => {
        if (typeof value !== "string" || !value.trim()) return
        try {
            const trimmed = value.trim()
            const url = new URL(/^[a-z]+:\/\//i.test(trimmed) ? trimmed : "https://" + trimmed)
            if (url.host) hosts.add(url.host.toLowerCase())
        } catch (e) { /* entrée inutilisable */ }
    }

    add(org.BASE_WEB_URL)
    add(org.domain)
    for (const virtual of Array.isArray(org.virtuals) ? org.virtuals : []) add(virtual?.domain)
    for (const devise of Array.isArray(org.devisesInterfaces) ? org.devisesInterfaces : []) add(devise?.domain)

    const web = org.web || {}
    add(web["embed.parentDomain"])
    const origins = web["embed.allowedOrigins"]
    const list = Array.isArray(origins) ? origins : String(origins || "").split(/[\n\r,;]+/)
    for (const entry of list) add(entry)

    return hosts
}

function hasDeclaredEmbedHosts(org = {}) {
    const web = org.web || {}
    if (typeof web["embed.parentDomain"] === "string" && web["embed.parentDomain"].trim()) return true
    const origins = web["embed.allowedOrigins"]
    if (Array.isArray(origins)) return origins.some((o) => String(o || "").trim())
    return String(origins || "").trim() !== ""
}

/**
 * Adresse de retour digne de confiance, ou `null`.
 *
 * POURQUOI ICI : cette valeur n'est pas qu'une donnée de formulaire — elle est
 * NAVIGUÉE par le tunnel (écran « lien expiré », page de succès). Comme la
 * configuration peut désormais arriver par l'URL, une valeur forgée y ferait
 * exécuter un `javascript:` dans l'origine de l'organisation, ou renverrait le
 * donateur sur un site tiers juste après sa saisie. On filtre donc au plus tôt,
 * avant que quiconque ne s'en serve.
 */
function safeReturnUrl(value, org = {}) {
    if (typeof value !== "string" || !value.trim()) return null

    let url
    try {
        url = new URL(value.trim())
    } catch (e) {
        return null
    }
    if (url.protocol !== "http:" && url.protocol !== "https:") return null

    if (!hasDeclaredEmbedHosts(org)) return url.href
    return orgReturnHosts(org).has(url.host.toLowerCase()) ? url.href : null
}

/**
 * Configuration du don pour la page /paiement, quelle que soit sa provenance :
 * l'URL d'abord (seul canal qui survit à la sortie de l'iframe), le stockage de
 * session ensuite (chemin historique, hors intégration).
 *
 * @param {object} org org du contexte, réinjectée dans la config
 */
export function loadPaiementConfig(org = {}) {
    if (typeof window === "undefined") return null

    try {
        const encoded = new URLSearchParams(window.location.search).get(PAIEMENT_CONFIG_PARAM)
        if (encoded) {
            const config = JSON.parse(fromBase64Url(encoded))

            // ── L'ORGANISATION NE VIENT JAMAIS DE L'URL ───────────────────────
            // Elle est TOUJOURS celle du rendu serveur, et l'éventuelle valeur
            // reçue est écrasée. C'est elle qui décide, côté API, du compte qui
            // encaisse et de l'association qui émet le reçu : une adresse forgée
            // ferait payer le donateur sur le site de son organisation, sous sa
            // marque, au profit de quelqu'un d'autre.
            return {
                ...config,
                organisation: getPurOrg(org),
                returnUrl: safeReturnUrl(config.returnUrl, org) || undefined,
            }
        }
    } catch (e) {
        logger.error({ err: e }, "[paiementLauncher] config d'URL illisible")
    }

    try {
        const stored = sessionStorage.getItem(PAIEMENT_STORAGE_KEY)
        if (!stored) return null
        const config = JSON.parse(stored)
        // Même filtre que pour l'URL : cette adresse sera navigée par le tunnel.
        return { ...config, returnUrl: safeReturnUrl(config.returnUrl, org) || undefined }
    } catch (e) {
        logger.error({ err: e }, "[paiementLauncher] sessionStorage getItem failed")
        return null
    }
}

// URL de la page paiement pour cette org (+ langue + query éventuelle, ex. theme).
export function buildPaiementUrl(org = {}, { lang, query, payload } = {}) {
    const base = (org && org.BASE_WEB_URL) || ""
    const langSeg = lang ? `/${lang}` : ""
    const params = new URLSearchParams(query ? query.replace(/^\?/, "") : "")

    if (payload) {
        const encoded = encodePaiementConfig(payload)
        if (encoded) params.set(PAIEMENT_CONFIG_PARAM, encoded)
    }

    const q = params.toString() ? `?${params.toString()}` : ""
    return `${base}${langSeg}/paiement${q}`
}

/**
 * Écrit le payload enrichi dans sessionStorage SANS rediriger. Utile pour les
 * call-sites en <Link> (Services/Abonnements) qui posent le payload dans onClick
 * et laissent Next gérer la navigation. Renvoie l'URL paiement à mettre en href.
 */
export function preparePaiement(config = {}, { org = {}, lang, query, forceReturnUrl } = {}) {
    const payload = buildPaiementPayload(config, org, { forceReturnUrl })
    try {
        if (typeof window !== "undefined") {
            sessionStorage.setItem(PAIEMENT_STORAGE_KEY, JSON.stringify(payload))
        }
    } catch (e) {
        logger.error({ err: e }, "[paiementLauncher] sessionStorage setItem failed")
    }
    // Intégré : la config part AUSSI dans l'URL, seul canal qui franchisse la
    // sortie de l'iframe (le stockage de session est cloisonné par site hôte).
    return buildPaiementUrl(org, { lang, query, payload: isEmbedded() ? payload : null })
}

/**
 * Point d'entrée principal : enrichit le payload, l'écrit en sessionStorage et
 * redirige vers la page paiement. Gère les deux modes de navigation rencontrés
 * dans les thèmes :
 *   - router (next/router) fourni → router.push (SPA, garde le contexte React)
 *   - sinon → window.location.href (hard nav, cas LinkMetaRender)
 *
 * @param {object} config payload partiel construit par le call-site
 * @param {object} ctx
 *   - ctx.org    : org du context (REQUIS pour BASE_WEB_URL + scheme)
 *   - ctx.router : instance next/router (optionnel)
 *   - ctx.lang   : code langue (optionnel)
 *   - ctx.query  : query string pour /paiement (ex. "theme=CAMPAGNE") (optionnel)
 *   - ctx.forceReturnUrl : fallback web explicite (liens externes) (optionnel)
 */
/**
 * Navigue vers une étape de paiement — la page /paiement, ou un lien court qui y
 * mène (soumission de formulaire, règlement d'une promesse).
 *
 * ── LA PAGE DE PAIEMENT NE VIT JAMAIS DANS UNE IFRAME ─────────────────────────
 * Intégré, on en sort ici. Le moment compte : appelée directement depuis un
 * gestionnaire de clic, la sortie passe par la fenêtre du haut (le geste de
 * l'utilisateur est encore valide). Appelée APRÈS un appel au serveur — le cas
 * des formulaires, de la réservation de lots ou d'un lien de règlement créé à la
 * demande — ce geste a expiré : le navigateur refuse alors la navigation du
 * haut, EN SILENCE. C'est pourquoi `navigateOut` demande d'abord au site hôte de
 * naviguer lui-même : une page qui se navigue elle-même n'a besoin d'aucun
 * geste, et ce chemin-là fonctionne quel que soit le délai écoulé.
 *
 * @param {string} url    destination
 * @param {object} router instance next/router (optionnel, hors intégration)
 */
export function navigateToPaiement(url, router) {
    if (!url) return url

    if (isEmbedded()) {
        navigateOut(url)
        return url
    }

    if (router && typeof router.push === "function") {
        router.push(url)
    } else if (typeof window !== "undefined") {
        window.location.href = url
    }
    return url
}

export function startPaiement(config = {}, { org = {}, router, lang, query, forceReturnUrl } = {}) {
    const url = preparePaiement(config, { org, lang, query, forceReturnUrl })

    // Panier trop volumineux pour l'URL (cf. encodePaiementConfig) : sortir de
    // l'iframe le perdrait — le stockage de session ne franchit pas la sortie —
    // et le donateur trouverait une page de paiement vide. On reste alors dans la
    // frame, où le panier est lisible : tunnel dégradé, mais don possible.
    if (isEmbedded() && !url.includes(PAIEMENT_CONFIG_PARAM + "=")) {
        if (router && typeof router.push === "function") {
            router.push(url)
        } else if (typeof window !== "undefined") {
            window.location.href = url
        }
        return url
    }

    return navigateToPaiement(url, router)
}

/**
 * Redirige vers la PLATEFORME de paiement (Stripe/Cardcom/PayGreen…) — l'URL GET
 * renvoyée par le backend (res.url de /paiements/offline/link).
 *   - Navigateur (web/desktop) → navigateTop() : la fenêtre du haut, avec repli
 *     sur la frame courante quand le navigateur refuse (iframe cross-origin).
 *   - App native (window.ISAPP) → on ouvre le NAVIGATEUR SYSTÈME via le bridge
 *     (message 'open.system' → Linking.openURL côté natif). On NE reste PAS dans
 *     la WebView ni un InAppBrowser : seul le navigateur système sait rouvrir l'app
 *     par Universal Link au retour (la page de succès, sur un autre domaine,
 *     redirige vers le domaine de l'org). Le returnUrl reste une URL web normale.
 *
 * @param {string} url URL de la plateforme de paiement (res.url).
 */
export function redirectToPlatform(url) {
    if (!url) return
    if (isApp()) {
        // Fire-and-forget : le natif ouvre Safari/Chrome ; la WebView reste en place
        // (l'utilisateur y revient par Universal Link après paiement).
        NativeBridge.post("open.system", { url })
        return
    }
    // top : casser un éventuel iframe d'intégration (embed).
    navigateTop(url)
}

/**
 * Navigue la fenêtre du HAUT vers `url`, avec repli sur la frame courante.
 *
 * POURQUOI le repli : dans une iframe d'une AUTRE origine, le navigateur refuse
 * de naviguer le top sans geste utilisateur récent — et il refuse SANS lever
 * d'exception ni rien casser : l'affectation paraît réussir, il ne se passe
 * rien. Le donateur restait alors bloqué sur l'écran de paiement, sans jamais
 * voir sa confirmation (il repaie, ou il appelle le support). On tente donc le
 * top (comportement historique, souhaitable : le tunnel doit sortir du cadre du
 * site hôte), puis on navigue la frame courante si la page est toujours là.
 */
export function navigateTop(url) {
    if (!url || typeof window === "undefined") return

    const inIframe = window.top !== window.self

    try {
        window.top.location.href = url
    } catch (e) {
        // Top d'une autre origine ET navigation refusée franchement : repli direct.
        logger.debug({ err: e }, "navigateTop: top refusé, repli sur la frame courante")
        window.location.href = url
        return
    }

    if (inIframe) {
        // La demande a pu être ignorée en silence : filet. Si le top navigue
        // vraiment, la page part avant que ce timer ne serve à quoi que ce soit.
        setTimeout(() => {
            try { window.location.href = url } catch (e) { /* noop */ }
        }, 800)
    }
}

export default {
    PAIEMENT_STORAGE_KEY,
    buildReturnUrl,
    resolveSource,
    buildPaiementPayload,
    buildPaiementUrl,
    loadPaiementConfig,
    navigateToPaiement,
    preparePaiement,
    startPaiement,
    redirectToPlatform,
    navigateTop,
}
